/**
 * AdminFilledContentBanner — shown on vendor / professional dashboards
 * when an admin filled their profile while they were inactive. Reads
 * `admin_filled_at` + `admin_filled_seen` from the user doc (returned by
 * /api/auth/me). One click dismisses it.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Info, X } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminFilledContentBanner() {
  const { user, checkAuth } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  if (!user) return null;
  if (dismissed) return null;
  if (user.admin_filled_seen) return null;
  if (!user.admin_filled_at) return null;

  const dismiss = async () => {
    setDismissed(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/me/dismiss-admin-filled-banner`,
        {},
        { withCredentials: true },
      );
      await checkAuth();
    } catch {
      // best-effort — banner is already hidden locally
    }
  };

  const note = user.admin_filled_note
    || 'BeeWeddy admin added content to your profile — please review & edit before customers see it.';

  return (
    <div
      className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3"
      data-testid="admin-filled-banner"
      role="status"
    >
      <Info size={20} weight="duotone" className="text-amber-700 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-amber-900 text-sm">Admin added content to your profile</div>
        <div className="text-xs text-amber-900/80 mt-0.5">{note}</div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="text-amber-800 hover:text-amber-950 flex-shrink-0"
        aria-label="Dismiss"
        data-testid="admin-filled-banner-dismiss"
      >
        <X size={16} weight="bold" />
      </button>
    </div>
  );
}
